import { FolderSidebarItem } from '@/components/folders/folder-sidebar-item';
import { ALL_FOLDERS, type FolderSelection, NO_FOLDER } from '@/lib/matrix-grid-search';
import type { MatrixFolder } from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';

export interface FolderSidebarProps {
  folders: readonly MatrixFolder[];
  /** Every matrix of the company. */
  totalCount: number;
  /** The matrices not in a folder. */
  looseCount: number;
  selection: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  onNewFolder: () => void;
  /** A matrix was dropped on a folder, or on « Hors dossier » (`null`). */
  onDropMatrix: (matrixId: string, folderId: string | null) => void;
  /** The folders could not be loaded: a discreet message with « Réessayer » shows in the column. */
  loadFailed?: boolean;
  onRetry?: () => void;
}

/**
 * The folders column on the left of the Matrices tab (issue #19, planche 14):
 * « Toutes les matrices », « Hors dossier », the folders in the order the API
 * gives (alphabetical, accents and case ignored), and « Nouveau dossier ».
 * Folders and « Hors dossier » take a dragged matrix; « Toutes les matrices »
 * does not, since a drop there would mean nothing.
 */
export function FolderSidebar({
  folders,
  totalCount,
  looseCount,
  selection,
  onSelect,
  onNewFolder,
  onDropMatrix,
  loadFailed = false,
  onRetry,
}: FolderSidebarProps) {
  return (
    <nav
      aria-label="Dossiers"
      className="flex w-full shrink-0 flex-col gap-0 border-gray-200 border-b pb-3 lg:w-[220px] lg:border-r lg:border-b-0 lg:pr-3 lg:pb-0"
    >
      <FolderSidebarItem
        icon={<Icon name="grid_view" size={16} />}
        label="Toutes les matrices"
        selected={selection === ALL_FOLDERS}
        count={totalCount}
        onSelect={() => onSelect(ALL_FOLDERS)}
      />
      <FolderSidebarItem
        icon={<Icon name="description" size={16} />}
        label="Hors dossier"
        selected={selection === NO_FOLDER}
        count={looseCount}
        onSelect={() => onSelect(NO_FOLDER)}
        onDropMatrix={(matrixId) => onDropMatrix(matrixId, null)}
      />
      <p className="px-2 pt-3 pb-1 font-semibold text-gray-500 text-micro uppercase tracking-wide">
        Dossiers
      </p>
      {loadFailed && (
        <p role="alert" className="px-2 py-1 text-caption text-gray-600">
          Les dossiers n'ont pas pu être chargés.{' '}
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-sm font-semibold text-bleu-nuit underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
            >
              Réessayer
            </button>
          )}
        </p>
      )}
      {folders.map((folder) => (
        <FolderSidebarItem
          key={folder.id}
          icon={<Icon name="folder" size={16} />}
          label={folder.name}
          selected={selection === folder.id}
          count={folder.matrixCount}
          noteCount={folder.openNoteCount}
          onSelect={() => onSelect(folder.id)}
          onDropMatrix={(matrixId) => onDropMatrix(matrixId, folder.id)}
        />
      ))}
      <FolderSidebarItem
        icon={<Icon name="add" size={16} />}
        label="Nouveau dossier"
        accent
        onSelect={onNewFolder}
      />
    </nav>
  );
}
